import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Typography, FormControl, Autocomplete
} from '@mui/material';
import MetaGlobal from '../../types/negocio/MetaGlobal';
import { metaGlobalService } from '../../services/MetaGlobalService';
import BasicModal from './BasicModal';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave?: (data: MetaGlobal) => void;
    metaGlobal: MetaGlobal;
    modoEdicao?: boolean;
    setMetaGlobal?: React.Dispatch<React.SetStateAction<MetaGlobal>>;
    onEditClick?: () => void;
    isNovaMetaGlobal?: boolean;
}

const MetaGlobalModal: React.FC<Props> = ({
    open,
    onClose,
    onSave,
    metaGlobal,
    modoEdicao = false,
    setMetaGlobal,
    onEditClick,
    isNovaMetaGlobal = false
}) => {
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [saving, setSaving] = useState(false);

    const camposObrigatorios = ['area', 'meta'];
    const [areasDisponiveis, setAreasDisponiveis] = useState<string[]>(['Vendas', 'Finanças', 'Processos / Produção', 'Pessoas']);

    useEffect(() => {
        if (open) {
            metaGlobalService.listarAreas().then((customAreas) => {
                const merged = new Set(['Vendas', 'Finanças', 'Processos / Produção', 'Pessoas', ...customAreas]);
                setAreasDisponiveis(Array.from(merged));
            }).catch(console.error);
        }
    }, [open]);

    // Validar campos obrigatórios
    const validarCampos = (): boolean => {
        const novosErros: Record<string, boolean> = {};
        
        if (modoEdicao && isNovaMetaGlobal) {
            camposObrigatorios.forEach(campo => {
                const valor = metaGlobal[campo as keyof MetaGlobal];
                if (!valor || valor.toString().trim() === '') {
                    novosErros[campo] = true;
                }
            });
        }
        
        setErrors(novosErros);
        return Object.keys(novosErros).length === 0;
    };

    // Limpar erros quando o modal fechar
    useEffect(() => {
        if (!open) {
            setErrors({});
        }
    }, [open]);

    const getModalTitle = () => {
        if (modoEdicao) {
            return isNovaMetaGlobal ? "Adicionar Meta Global" : "Editar Meta Global";
        }
        return "Detalhes da Meta Global";
    };

    const handleChange = (field: keyof MetaGlobal, value: string | number) => {
        if (!setMetaGlobal) return;
        setMetaGlobal(prev => ({ ...prev, [field]: value }));
        
        // Limpar erro do campo quando ele for preenchido
        if (errors[field] && value.toString().trim() !== '') {
            setErrors(prev => ({ ...prev, [field]: false }));
        }
    };

    const renderCampo = (label: string, field: keyof MetaGlobal, multiline = false) => {
        const isObrigatorio = modoEdicao && isNovaMetaGlobal && camposObrigatorios.includes(field);
        const temErro = errors[field] || false;
        
        if (field === 'area') {
            const valor = metaGlobal.area;
            if (!modoEdicao && (!valor || valor.toString().trim() === '')) return null;

            return (
                <Box sx={{ width: '100%', px: 1, mb: 2 }}>
                    {modoEdicao ? (
                        <FormControl fullWidth error={temErro}>
                            <Autocomplete
                                freeSolo
                                options={areasDisponiveis}
                                value={valor || ''}
                                onChange={(_, newValue) => handleChange('area', newValue || '')}
                                onInputChange={(_, newInputValue) => handleChange('area', newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={isObrigatorio ? 'Área *' : 'Área'}
                                        variant="outlined"
                                        color="secondary"
                                        error={temErro}
                                    />
                                )}
                            />
                            {temErro && (
                                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.5 }}>
                                    Este campo é obrigatório
                                </Typography>
                            )}
                        </FormControl>
                    ) : (
                        <>
                            <Typography fontSize={12} sx={{ color: "#607C90" }}>
                                {label}
                            </Typography>
                            <Typography fontSize={16}>
                                {valor}
                            </Typography>
                        </>
                    )}
                </Box>
            );
        }

        const valor = metaGlobal[field];
        if (!modoEdicao && (!valor || valor.toString().trim() === '')) return null;

        return (
            <Box sx={{ width: '100%', px: 1, mb: 2 }}>
                {modoEdicao ? (
                    <TextField
                        fullWidth
                        label={label}
                        variant="outlined"
                        color="secondary"
                        value={valor || ''}
                        onChange={(e) => handleChange(field, e.target.value)}
                        multiline={multiline}
                        rows={multiline ? 3 : 1}
                        error={temErro}
                        helperText={temErro ? 'Este campo é obrigatório' : ''}
                        required={isObrigatorio}
                    />
                ) : (
                    <>
                        <Typography fontSize={12} sx={{ color: "#607C90" }}>
                            {label}
                        </Typography>
                        <Typography fontSize={16}>
                            {valor}
                        </Typography>
                    </>
                )}
            </Box>
        );
    };

    return (
        <BasicModal
            title={getModalTitle()}
            open={open}
            onClose={onClose}
            saveButton={modoEdicao}
            onSave={async () => {
                if (modoEdicao && isNovaMetaGlobal) {
                    if (!validarCampos()) return;
                }
                if (!onSave) return;
                setSaving(true);
                try {
                    await onSave(metaGlobal);
                } finally {
                    setSaving(false);
                }
            }}
            saveLoading={saving}
            onEdit={onEditClick}
            tela='meta-global'
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                {renderCampo('Área', 'area')}
                {renderCampo('Meta', 'meta', true)}
            </Box>
        </BasicModal>
    );
};

export default MetaGlobalModal;